"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CHOICE_TYPES = exports.METAFIELD_ENTITIES = exports.METAFIELD_TYPES = void 0;
exports.metafieldKeyFrom = metafieldKeyFrom;
exports.isValidMetafieldKey = isValidMetafieldKey;
exports.duplicateKey = duplicateKey;
exports.blankValue = blankValue;
exports.isBlank = isBlank;
exports.validateValue = validateValue;
exports.validateValues = validateValues;
const text_1 = require("../text");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Metafields: the key generator and the nine types' validation.             │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **Both halves are here because both halves run TWICE.** The form validates so somebody sees a
 * problem beside the field they typed it in; the server validates because that is the only check
 * that counts. Two implementations of "is this a valid date for this field" is how a screen comes to
 * accept what the API rejects — and with nine types and five entities there are forty-five chances
 * to get that wrong.
 */
exports.METAFIELD_TYPES = [
    'text',
    'textarea',
    'number',
    'date',
    'select',
    'multi',
    'checkbox',
    'user',
    'url',
];
/**
 * ⚠️ **Seven, and the last two are C30's** — `contract` and `lineitem`, migration `0057`. The
 * engine did not change to take them: a metafield describes a row, and these are two more kinds of
 * row.
 */
exports.METAFIELD_ENTITIES = [
    'task',
    'person',
    'project',
    'client',
    /* C56 · a supplier, a cost type, their invoice number — the mockup's own three. */
    'external_cost',
    /* C57 · teljesítésigazolás, fizetési mód, ügyfél rendelésszáma — the mockup's own three. */
    'invoice',
    'worklog',
    'contract',
    'lineitem',
];
/** The two types whose value must be one of a listed set. */
exports.CHOICE_TYPES = ['select', 'multi'];
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️ HUNGARIAN ACCENT FOLDING lives in `../text`, and did so on its second   │
 * │    caller rather than its third.                                          │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `Ügyfél hivatkozás` → `ugyfel_hivatkozas`. The map and the two traps it exists for — the
 * lower-case-before-fold ordering, and `ő`/`ű` defeating a generic `NFD` fold — are documented on
 * `foldHungarian` itself. They moved out of this file when tenant provisioning needed the same
 * fold with a different separator; the alternative was a second copy that would have got exactly
 * one of those two wrong.
 *
 * ⚠️ **The result can be EMPTY**, and the caller has to handle it: a name of `"???"` folds to `""`,
 * which is not a valid key. Returning `''` rather than a fabricated `field_1` keeps the decision
 * where the error message can be written.
 */
function metafieldKeyFrom(name) {
    return ((0, text_1.foldHungarian)(name)
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        /* ⚠️ 40, matching the column's own check. Truncating here rather than letting the database
         * refuse means the generated key is always storable — the person never sees a refusal for a
         * key they did not choose. */
        .slice(0, 40));
}
/** The shape the column enforces, so the client can refuse before the round trip. */
function isValidMetafieldKey(key) {
    return /^[a-z][a-z0-9_]{0,39}$/.test(key);
}
/**
 * ⚠️ **A duplicate suffix that walks, exactly as the mockup's does.** `ugyfel_hivatkozas` →
 * `ugyfel_hivatkozas_masolat` → `_masolat2`. The base is stripped first, so duplicating a duplicate
 * gives `_masolat2` rather than `_masolat_masolat`.
 */
function duplicateKey(key, taken) {
    const base = key.replace(/_masolat\d*$/, '');
    let candidate = `${base}_masolat`;
    let n = 2;
    while (taken(candidate)) {
        candidate = `${base}_masolat${String(n)}`;
        n += 1;
    }
    /* ⚠️ Truncated, like the generator: `_masolat` on a 40-character key would otherwise produce one
     * the column refuses. Truncating the BASE keeps the suffix, which is the part carrying meaning. */
    if (candidate.length <= 40)
        return candidate;
    const suffix = candidate.slice(base.length);
    return base.slice(0, 40 - suffix.length) + suffix;
}
/**
 * The blank value for a type.
 *
 * ⚠️ **Three different blanks, and collapsing them to `''` breaks two types.** A multi's blank is
 * `[]` because its control maps over it; a checkbox's is `false` because `''` is falsy but not a
 * boolean and would render an indeterminate box. The mockup's `metaBlank` makes the same split.
 */
function blankValue(type) {
    if (type === 'multi')
        return [];
    if (type === 'checkbox')
        return false;
    return '';
}
/**
 * Whether a value counts as "not filled in" — which is what `required` is about.
 *
 * ⚠️ **`| undefined` in the signature, and lint is what made it honest.** `MetafieldValue` does not
 * include `undefined`, so the `undefined` branch read as unreachable — and it is reached on every
 * form that omits a key entirely, which is the common case for a field nobody touched. Widening the
 * parameter states what this function actually receives; deleting the branch to satisfy the rule
 * would have made a missing key throw on `String(undefined)` further down.
 */
function isBlank(value) {
    if (value === null || value === undefined)
        return true;
    if (Array.isArray(value))
        return value.length === 0;
    if (typeof value === 'boolean')
        return false;
    return String(value).trim() === '';
}
/**
 * Validate and normalise one value against its field.
 *
 * ⚠️ **It NORMALISES as well as validating**, so the stored value is canonical: a number arrives as
 * `"8,5"` from a Hungarian keyboard and is stored as `8.5`; a trimmed string is stored trimmed. A
 * validator that only says yes or no leaves every caller to normalise, and they will disagree.
 *
 * ⚠️ **A required CHECKBOX is not checked for blankness**, and the mockup does the same
 * (`f.req && f.type !== 'checkbox'`). "Required" on a boolean would mean *"must be true"*, which is
 * a different and much stranger rule than *"must be answered"* — and `false` is an answer.
 */
function validateValue(field, raw) {
    const value = raw;
    if (isBlank(value)) {
        if (field.required && field.type !== 'checkbox')
            return { ok: false, reason: 'required' };
        /* ⚠️ A blank stays blank rather than becoming the type's zero: storing `0` for an unanswered
         * number is a number somebody will later read as a measurement. */
        return { ok: true, value: field.type === 'checkbox' ? false : null };
    }
    if (field.type === 'checkbox') {
        if (typeof value !== 'boolean')
            return { ok: false, reason: 'wrong_shape' };
        return { ok: true, value };
    }
    if (field.type === 'multi') {
        if (!Array.isArray(value))
            return { ok: false, reason: 'wrong_shape' };
        const chosen = value.map((entry) => String(entry));
        const offending = chosen.find((entry) => !field.options.includes(entry));
        if (offending !== undefined)
            return { ok: false, reason: 'not_an_option', offending };
        /* ⚠️ De-duplicated, and kept in the FIELD's option order rather than the order they were
         * clicked. Two rows holding the same set in different orders would compare unequal in every
         * export and every report. */
        return { ok: true, value: field.options.filter((option) => chosen.includes(option)) };
    }
    if (field.type === 'select') {
        const chosen = String(value);
        if (!field.options.includes(chosen)) {
            return { ok: false, reason: 'not_an_option', offending: chosen };
        }
        return { ok: true, value: chosen };
    }
    if (field.type === 'number') {
        /* ⚠️ The decimal comma again — a Hungarian keyboard produces `8,5` by default, and refusing it
         * refuses the majority of the input on the primary locale. Same reasoning as `hours.ts`. */
        const text = String(value).trim().replace(',', '.');
        if (!/^-?\d+(\.\d+)?$/.test(text))
            return { ok: false, reason: 'not_a_number' };
        return { ok: true, value: Number(text) };
    }
    if (field.type === 'date') {
        const text = String(value).trim();
        if (!/^\d{4}-\d{2}-\d{2}$/.test(text))
            return { ok: false, reason: 'not_a_date' };
        /**
         * ⚠️ **`2026-02-30` matches the pattern and is not a day.** `dates.ts` records that as *"how a
         * typo in a filter becomes a silent off-by-two"*; the round trip is the only cheap check, and
         * skipping it here would store a date no calendar contains.
         */
        const [year, month, day] = text.split('-').map(Number);
        const parsed = new Date(year ?? 0, (month ?? 1) - 1, day ?? 1);
        const roundTrip = parsed.getFullYear() === year &&
            parsed.getMonth() === (month ?? 1) - 1 &&
            parsed.getDate() === day;
        if (!roundTrip)
            return { ok: false, reason: 'not_a_date' };
        return { ok: true, value: text };
    }
    if (field.type === 'url') {
        const text = String(value).trim();
        /**
         * ⚠️ **`http(s)` ONLY, and that is a security rule rather than a formatting one.** The value
         * ends up in an `href`, so accepting a scheme means accepting whatever that scheme does —
         * `javascript:` is the obvious one and `data:` is the one people forget. An allow-list of two.
         */
        if (!/^https?:\/\/[^\s]+$/i.test(text))
            return { ok: false, reason: 'not_a_url' };
        return { ok: true, value: text };
    }
    if (field.type === 'user') {
        /* A member id. Whether that member exists is the server's question — it has the table. */
        const text = String(value).trim();
        if (!/^[0-9a-f-]{36}$/i.test(text))
            return { ok: false, reason: 'wrong_shape' };
        return { ok: true, value: text };
    }
    /* `text` and `textarea`. Trimmed, so a value of spaces is blank rather than stored. */
    return { ok: true, value: String(value).trim() };
}
/**
 * Validate a whole form's worth.
 *
 * ⚠️ **Returns EVERY refusal, keyed by field.** Returning the first one makes a form with three
 * problems take three round trips to fix, which `state-rules.md` calls out for exactly this shape.
 */
function validateValues(fields, values) {
    const accepted = {};
    const errors = {};
    for (const field of fields) {
        const result = validateValue(field, values[field.key]);
        if (result.ok)
            accepted[field.key] = result.value;
        else
            errors[field.key] = result;
    }
    return { values: accepted, errors };
}
//# sourceMappingURL=metafields.js.map